import { useState, useEffect } from "react";
import api from "../../../utils/api";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    CircularProgress,
    Stack,
} from "@mui/material";
import { toDateTimeLocal, toServerDateTime, teacherLabelFromClassroom } from "../calendarUtils";
import {
    CALL_TYPE_GOOGLE_MEET,
    CALL_TYPE_LANGUAGEON,
    CALL_TYPE_OFFLINE,
    CALL_TYPE_OPTIONS,
} from "../lessonCallTypes";

function addMinutesToDateTimeLocal(dtLocal, minutes) {
    if (!dtLocal) return "";
    const dt = new Date(dtLocal);
    if (Number.isNaN(dt.getTime())) return "";
    dt.setMinutes(dt.getMinutes() + minutes);
    const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

const initialForm = {
    classroom_id: "",
    starts_at: "",
    ends_at: "",
    status: "planned",
    call_type: CALL_TYPE_LANGUAGEON,
    call_link: "",
    notes: "",
};

/**
 * @param {object|null} editLesson — when set, modal edits via PUT (id, classroom_id, starts_at/ends_at ISO, status, call_link, notes).
 */
export default function CreateLessonModal({
    open,
    onClose,
    classrooms = [],
    defaultClassroomId = "",
    initialStartsAt = "",
    initialEndsAt = "",
    editLesson = null,
    onDelete,
}) {
    const [saving, setSaving] = useState(false);
    const [joining, setJoining] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(initialForm);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "call_type" && (value === CALL_TYPE_LANGUAGEON || value === CALL_TYPE_OFFLINE)) {
                next.call_link = "";
            }
            return next;
        });
    };

    useEffect(() => {
        if (!open) return;
        if (editLesson?.id) {
            const ct = editLesson.call_type || CALL_TYPE_LANGUAGEON;
            setForm({
                classroom_id: editLesson.classroom_id != null ? String(editLesson.classroom_id) : "",
                starts_at: editLesson.starts_at ? toDateTimeLocal(editLesson.starts_at) : "",
                ends_at: editLesson.ends_at ? toDateTimeLocal(editLesson.ends_at) : "",
                status: editLesson.status || "planned",
                call_type: ct,
                call_link:
                    ct === CALL_TYPE_LANGUAGEON || ct === CALL_TYPE_OFFLINE
                        ? ""
                        : (editLesson.call_link ?? ""),
                notes: editLesson.notes ?? "",
            });
            setErrors({});
            setSaving(false);
            setJoining(false);
            return;
        }
        const start = initialStartsAt ? toDateTimeLocal(initialStartsAt) : "";
        const end = initialEndsAt
            ? toDateTimeLocal(initialEndsAt)
            : start
              ? addMinutesToDateTimeLocal(start, 60)
              : "";
        setForm({
            ...initialForm,
            classroom_id: defaultClassroomId ? String(defaultClassroomId) : "",
            starts_at: start,
            ends_at: end,
        });
        setErrors({});
        setSaving(false);
        setJoining(false);
    }, [open, editLesson, defaultClassroomId, initialStartsAt, initialEndsAt]);

    const handleSubmit = async () => {
        setErrors({});
        if (form.call_type === CALL_TYPE_GOOGLE_MEET && !String(form.call_link || "").trim()) {
            setErrors({ call_link: ["Meeting link is required for Google Meet."] });
            return;
        }
        if (form.starts_at && form.ends_at) {
            const startMs = new Date(form.starts_at).getTime();
            const endMs = new Date(form.ends_at).getTime();
            if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
                const diffMinutes = (endMs - startMs) / 60000;
                if (diffMinutes <= 0) {
                    setErrors({ ends_at: ["End must be after start."] });
                    return;
                }
                if (diffMinutes > 240) {
                    setErrors({ ends_at: ["A lesson cannot be longer than 4 hours."] });
                    return;
                }
            }
        }
        try {
            setSaving(true);
            const starts = toServerDateTime(form.starts_at);
            const ends = toServerDateTime(form.ends_at);
            const payload = {
                classroom_id: Number(form.classroom_id),
                starts_at: starts,
                ends_at: ends,
                status: form.status || "planned",
                call_type: form.call_type || CALL_TYPE_LANGUAGEON,
                call_link:
                    form.call_type === CALL_TYPE_GOOGLE_MEET
                        ? String(form.call_link || "").trim() || null
                        : null,
                notes: form.notes || null,
            };
            if (editLesson?.id) {
                await api.put(`admin/classroom-lessons/${editLesson.id}`, payload);
            } else {
                await api.post("admin/classroom-lessons", payload);
            }
            onClose(true);
        } catch (e) {
            const errData = e.response?.data || e;
            setErrors(errData.errors || {});
        } finally {
            setSaving(false);
        }
    };

    const handleJoin = async () => {
        if (!editLesson?.id || joining) return;

        const callType = editLesson.call_type || "";
        if (callType === CALL_TYPE_GOOGLE_MEET) {
            const link = String(editLesson.call_link || "").trim();
            if (!/^https?:\/\//i.test(link)) {
                window.alert("This lesson has no meeting link.");
                return;
            }
            window.open(link, "_blank", "noopener,noreferrer");
            return;
        }

        try {
            setJoining(true);
            const response = await api.post(`admin/classroom-lessons/${editLesson.id}/join-link`);
            const url = response?.data?.data?.url;
            if (!url) {
                window.alert("Failed to open the lesson.");
                return;
            }
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (e) {
            const bag = e?.errors || e?.response?.data?.errors;
            const first = bag && typeof bag === "object" ? Object.values(bag).flat()[0] : null;
            window.alert(first || e?.response?.data?.message || "Failed to open the lesson.");
        } finally {
            setJoining(false);
        }
    };

    const endsError = errors.ends_at?.[0];
    const savedCallType = editLesson?.call_type || "";
    const savedCallLink = String(editLesson?.call_link || "").trim();
    const lessonIsInProgress = String(editLesson?.status || "").toLowerCase() === "in_progress";
    const canJoinLesson = Boolean(editLesson?.id) && lessonIsInProgress && (
        savedCallType === CALL_TYPE_LANGUAGEON
        || (savedCallType === CALL_TYPE_GOOGLE_MEET && savedCallLink !== "")
    );

    const selectedClassroom = form.classroom_id
        ? classrooms.find((c) => String(c.id) === String(form.classroom_id))
        : null;
    const teacherDisplayName = teacherLabelFromClassroom(selectedClassroom);

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{editLesson?.id ? `Edit lesson #${editLesson.id}` : "Create lesson"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {editLesson?.id ? (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            justifyContent="center"
                            alignItems="center"
                            sx={{ mb: 1, flexWrap: "wrap" }}
                        >
                            {canJoinLesson ? (
                                <Button
                                    variant="contained"
                                    onClick={handleJoin}
                                    disabled={saving || joining}
                                    sx={{ minWidth: 160 }}
                                >
                                    {joining ? <CircularProgress size={22} color="inherit" /> : "Join"}
                                </Button>
                            ) : null}
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => onDelete?.(editLesson.id, editLesson.status)}
                                disabled={saving || joining}
                                sx={{ minWidth: 160, borderWidth: 2 }}
                            >
                                Delete lesson
                            </Button>
                        </Stack>
                    ) : null}

                    <FormControl error={!!errors.classroom_id?.[0]}>
                        <FormLabel htmlFor="create_lesson_classroom">Classroom</FormLabel>
                        <Select
                            id="create_lesson_classroom"
                            name="classroom_id"
                            value={form.classroom_id}
                            onChange={handleChange}
                            error={!!errors.classroom_id?.[0]}
                        >
                            {classrooms.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                    {c.name || c.classroom_name || `Classroom #${c.id}`}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.classroom_id && (
                            <FormHelperText error>{errors.classroom_id[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormLabel htmlFor="create_lesson_teacher">Teacher</FormLabel>
                        <OutlinedInput
                            id="create_lesson_teacher"
                            value={teacherDisplayName}
                            readOnly
                            fullWidth
                        />
                        <FormHelperText>Set on the classroom; change there to reassign.</FormHelperText>
                    </FormControl>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                        <FormControl error={!!errors.starts_at?.[0]} sx={{ flex: 1, width: "100%" }}>
                            <FormLabel htmlFor="create_lesson_starts_at">Start</FormLabel>
                            <OutlinedInput
                                id="create_lesson_starts_at"
                                name="starts_at"
                                type="datetime-local"
                                fullWidth
                                value={form.starts_at}
                                onChange={handleChange}
                            />
                            {errors.starts_at && (
                                <FormHelperText error>{errors.starts_at[0]}</FormHelperText>
                            )}
                        </FormControl>
                        <FormControl error={!!endsError} sx={{ flex: 1, width: "100%" }}>
                            <FormLabel htmlFor="create_lesson_ends_at">End</FormLabel>
                            <OutlinedInput
                                id="create_lesson_ends_at"
                                name="ends_at"
                                type="datetime-local"
                                fullWidth
                                value={form.ends_at}
                                onChange={handleChange}
                            />
                            {endsError && <FormHelperText error>{endsError}</FormHelperText>}
                        </FormControl>
                    </Stack>

                    <FormControl error={!!errors.status?.[0]}>
                        <FormLabel htmlFor="create_lesson_status">Status</FormLabel>
                        <Select
                            id="create_lesson_status"
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                        >
                            <MenuItem value="planned">Planned</MenuItem>
                            <MenuItem value="in_progress">In progress</MenuItem>
                            <MenuItem value="done">Done</MenuItem>
                            <MenuItem value="canceled">Canceled</MenuItem>
                        </Select>
                        {errors.status && <FormHelperText error>{errors.status[0]}</FormHelperText>}
                    </FormControl>

                    <FormControl error={!!errors.call_type?.[0]}>
                        <FormLabel htmlFor="create_lesson_call_type">Call type</FormLabel>
                        <Select
                            id="create_lesson_call_type"
                            name="call_type"
                            value={form.call_type}
                            onChange={handleChange}
                        >
                            {CALL_TYPE_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.call_type && <FormHelperText error>{errors.call_type[0]}</FormHelperText>}
                    </FormControl>

                    {form.call_type === CALL_TYPE_GOOGLE_MEET ? (
                        <FormControl error={!!errors.call_link?.[0]}>
                            <FormLabel htmlFor="create_lesson_call_link">Meeting link</FormLabel>
                            <OutlinedInput
                                id="create_lesson_call_link"
                                name="call_link"
                                fullWidth
                                value={form.call_link}
                                onChange={handleChange}
                                placeholder="https://meet.google.com/…"
                            />
                            <FormHelperText>Required for Google Meet.</FormHelperText>
                            {errors.call_link && (
                                <FormHelperText error>{errors.call_link[0]}</FormHelperText>
                            )}
                        </FormControl>
                    ) : null}
                    {form.call_type === CALL_TYPE_OFFLINE ? (
                        <FormHelperText sx={{ mt: -1 }}>
                            A lesson reference link is generated automatically after saving.
                        </FormHelperText>
                    ) : null}

                    <FormControl error={!!errors.notes?.[0]}>
                        <FormLabel htmlFor="create_lesson_notes">Notes</FormLabel>
                        <OutlinedInput
                            id="create_lesson_notes"
                            name="notes"
                            fullWidth
                            multiline
                            minRows={3}
                            value={form.notes}
                            onChange={handleChange}
                        />
                        {errors.notes && <FormHelperText error>{errors.notes[0]}</FormHelperText>}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                {editLesson?.id ? (
                    <Button
                        color="error"
                        onClick={() => onDelete?.(editLesson.id, editLesson.status)}
                        disabled={saving}
                    >
                        Delete
                    </Button>
                ) : null}
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                    {saving ? <CircularProgress size={24} /> : editLesson?.id ? "Save" : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
